import React, { useState, useEffect } from 'react';
import { serviceItemAPI, unitAPI, categoryAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { DataTable } from '../components/DataTable';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Briefcase, 
  Plus, 
  Loader2, 
  Pencil, 
  Trash2,
  Search
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../lib/utils';

const ServiceItemsPage = () => {
  const [serviceItems, setServiceItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    default_unit_id: '',
    default_rate: '',
    hsn_sac_code: '',
    gst_rate: '0',
    tds_applicable: false,
    tds_rate: '',
    tds_section: '',
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [itemsRes, unitsRes, categoriesRes] = await Promise.all([
        serviceItemAPI.getAll({ is_active: true }),
        unitAPI.getAll(),
        categoryAPI.getAll()
      ]);
      setServiceItems(itemsRes.data);
      setUnits(unitsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error('Failed to load service items');
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = serviceItems.filter(item => {
    const matchesSearch = !searchQuery || 
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = filterCategory === 'all' || item.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const handleOpenCreate = () => {
    setEditingItem(null);
    setFormData({
      name: '',
      description: '',
      category: '',
      default_unit_id: '',
      default_rate: '',
      hsn_sac_code: '',
      gst_rate: '0',
      tds_applicable: false,
      tds_rate: '',
      tds_section: '',
      is_active: true
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      description: item.description || '',
      category: item.category || '',
      default_unit_id: item.default_unit_id || '',
      default_rate: item.default_rate?.toString() || '',
      hsn_sac_code: item.hsn_sac_code || '',
      gst_rate: item.gst_rate?.toString() || '0',
      tds_applicable: item.tds_applicable || false,
      tds_rate: item.tds_rate?.toString() || '',
      tds_section: item.tds_section || '',
      is_active: item.is_active !== false
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Service name is required');
      return;
    }

    try {
      setSaving(true);
      const dataToSend = {
        ...formData,
        default_rate: parseFloat(formData.default_rate) || 0,
        gst_rate: formData.gst_rate !== '' ? parseInt(formData.gst_rate) : 0,
        tds_applicable: formData.tds_applicable || false,
        tds_rate: formData.tds_applicable && formData.tds_rate ? parseFloat(formData.tds_rate) : 0,
        tds_section: formData.tds_applicable ? formData.tds_section : null,
        default_unit_id: formData.default_unit_id || null,
        category: formData.category || null
      };

      if (editingItem) {
        await serviceItemAPI.update(editingItem.id, dataToSend);
        toast.success('Service item updated');
      } else {
        await serviceItemAPI.create(dataToSend);
        toast.success('Service item created');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save service item');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try {
      await serviceItemAPI.delete(item.id);
      toast.success('Service item deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="space-y-6" data-testid="service-items-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-2">
            <Briefcase className="h-7 w-7" />
            Service Items Master
          </h1>
          <p className="text-muted-foreground mt-1">Manage your service catalog for Service POs</p>
        </div>
        <Button onClick={handleOpenCreate} data-testid="add-service-item-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Service Item
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search services..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-full md:w-[200px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Service Items Table */}
      <Card>
        <CardHeader>
          <CardTitle>Service Items ({filteredItems.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            module="service-items"
            data={filteredItems}
            loading={loading}
            title="Service Items Report"
            columns={[
              { key: 'name', label: 'Service Name', render: (val, item) => (
                <div>
                  <p className="font-medium">{val}</p>
                  {item.description && (
                    <p className="text-xs text-muted-foreground truncate max-w-[200px]">{item.description}</p>
                  )}
                </div>
              )},
              { key: 'category', label: 'Category', render: (val) => val ? (
                <Badge variant="outline">{val}</Badge>
              ) : '-' },
              { key: 'default_unit_name', label: 'Unit', render: (val) => val || '-' },
              { key: 'default_rate', label: 'Default Rate', render: (val) => formatCurrency(val || 0) },
              { key: 'gst_rate', label: 'GST', render: (val) => `${val ?? 0}%` },
              { key: 'tds_applicable', label: 'TDS', render: (val, item) => val ? (
                <div className="text-xs">
                  <Badge variant="outline" className="text-orange-600">{item.tds_rate || 0}%</Badge>
                  {item.tds_section && <span className="ml-1 text-muted-foreground">{item.tds_section}</span>}
                </div>
              ) : <span className="text-muted-foreground">-</span> },
              { key: 'hsn_sac_code', label: 'SAC Code', render: (val) => val || '-' },
              { key: 'is_active', label: 'Status', render: (val) => (
                <Badge variant={val !== false ? 'default' : 'secondary'}>
                  {val !== false ? 'Active' : 'Inactive'}
                </Badge>
              )},
            ]}
            renderActions={(item) => (
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(item)} title="Edit">
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(item)} title="Delete">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}
          />
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[600px]">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Service Item' : 'Add Service Item'}</DialogTitle>
            <DialogDescription>
              {editingItem ? 'Update service details' : 'Add a new service to your catalog'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label>Service Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., HVAC Maintenance"
                  data-testid="service-name-input"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the service..."
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={formData.category || "none"}
                  onValueChange={(val) => setFormData({ ...formData, category: val === "none" ? "" : val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Category</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Default Unit</Label>
                <Select
                  value={formData.default_unit_id || "none"}
                  onValueChange={(val) => setFormData({ ...formData, default_unit_id: val === "none" ? "" : val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Default</SelectItem>
                    {units.map(unit => (
                      <SelectItem key={unit.id} value={unit.id}>
                        {unit.name} {unit.symbol ? `(${unit.symbol})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Default Rate (₹)</Label>
                <Input
                  type="number"
                  value={formData.default_rate}
                  onChange={(e) => setFormData({ ...formData, default_rate: e.target.value })}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="space-y-2">
                <Label>GST Rate (%)</Label>
                <Select
                  value={formData.gst_rate}
                  onValueChange={(val) => setFormData({ ...formData, gst_rate: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0%</SelectItem>
                    <SelectItem value="5">5%</SelectItem>
                    <SelectItem value="12">12%</SelectItem>
                    <SelectItem value="18">18% (ITC Eligible)</SelectItem>
                    <SelectItem value="28">28%</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>SAC Code</Label>
                <Input
                  value={formData.hsn_sac_code}
                  onChange={(e) => setFormData({ ...formData, hsn_sac_code: e.target.value })}
                  placeholder="e.g., 998719"
                />
              </div>
            </div>

            {/* TDS Section */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center gap-2 mb-4">
                <input
                  type="checkbox"
                  id="tds_applicable"
                  checked={formData.tds_applicable}
                  onChange={(e) => setFormData({ ...formData, tds_applicable: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="tds_applicable" className="font-medium cursor-pointer">
                  TDS Applicable
                </Label>
              </div>

              {formData.tds_applicable && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6 border-l-2 border-orange-200">
                  <div className="space-y-2">
                    <Label>TDS Rate (%)</Label>
                    <Select
                      value={formData.tds_rate || "0"}
                      onValueChange={(val) => setFormData({ ...formData, tds_rate: val })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select TDS rate" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1%</SelectItem>
                        <SelectItem value="2">2%</SelectItem>
                        <SelectItem value="5">5%</SelectItem>
                        <SelectItem value="10">10%</SelectItem>
                        <SelectItem value="20">20%</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>TDS Section</Label>
                    <Select
                      value={formData.tds_section || "none"}
                      onValueChange={(val) => setFormData({ ...formData, tds_section: val === "none" ? "" : val })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select section" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not Specified</SelectItem>
                        <SelectItem value="194C">194C - Contractor</SelectItem>
                        <SelectItem value="194J">194J - Professional/Technical Services</SelectItem>
                        <SelectItem value="194H">194H - Commission/Brokerage</SelectItem>
                        <SelectItem value="194I">194I - Rent</SelectItem>
                        <SelectItem value="194A">194A - Interest (other than securities)</SelectItem>
                        <SelectItem value="194D">194D - Insurance Commission</SelectItem>
                        <SelectItem value="194M">194M - Payment to Contractor (Individuals/HUF)</SelectItem>
                        <SelectItem value="194O">194O - E-commerce Operator</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingItem ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiceItemsPage;
